import { ReactNode } from 'react';

import { PresentationVariableNode } from '@/components/TextEditor/plugins/PresentationVariablePlugin/PresentationVariableNode';
import { PresentationVariablePlugin } from '@/components/TextEditor/plugins/PresentationVariablePlugin/PresentationVariablePlugin';
import { usePresentationStringFormatter } from '@/modules/presentation/clientPresentation/presentationString/usePresentationStringFormatter';

import { useResolverVersion } from './useResolverVersion';

const VARIABLE_HIGHLIGHT_NODES = [PresentationVariableNode];

/**
 * Bundles the Lexical node registrations and plugins needed for
 * presentation-variable highlighting in the rich text slide editor.
 *
 * - `nodes` — always includes `PresentationVariableNode` so the editor
 *   can render variable spans with the CSS class.
 * - `displayPlugins` — resolves `{Variable}` tokens to their display
 *   values (read-only / display mode).
 * - `editPlugins` — highlights raw `{Variable}` tokens without resolving
 *   them (edit mode).
 * - `resolverVersion` — increments when the resolver function changes
 *   (e.g. household data loads), useful as a React `key` to force
 *   re-mount.
 */
export function useVariableHighlight(): {
  nodes: typeof VARIABLE_HIGHLIGHT_NODES;
  displayPlugins: ReactNode;
  editPlugins: ReactNode;
  resolverVersion: number;
} {
  const { resolveVariable } = usePresentationStringFormatter();
  const resolverVersion = useResolverVersion(resolveVariable);

  // Always register the variable node type and plugin so the Lexical editor
  // can render PresentationVariableNodes for CSS-based highlighting. The parent
  // Box controls whether the background is always-on (templates) or hover-only
  // (live presentations).
  const displayPlugins = (
    <PresentationVariablePlugin resolveVariable={resolveVariable} />
  );

  // Edit mode: always highlight raw {Variable} tokens so they're visually
  // distinct. Safe because onUpdate returns plain markdown, not styled nodes.
  const editPlugins = <PresentationVariablePlugin />;

  return {
    nodes: VARIABLE_HIGHLIGHT_NODES,
    displayPlugins,
    editPlugins,
    resolverVersion,
  };
}
